import React, { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import {
  useActivateProjectMutation,
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useGetCustomTemplatesQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useGetProjectByIdQuery,
} from 'src/services';
import moment from 'moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { useDispatch } from 'react-redux';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import { CreateProjectManualStep4 } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CreateProjectManualStep4';
import { CreateProjectPromptBuilderStep4 } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CreateProjectPromptBuilderStep4';
import { clsx } from 'clsx';
import { API_VERSION } from 'src/constants/api-routes';
import { useNotification } from 'src/hooks/useNotification';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-60'} />
    </div>
  );
};

const CreateProjectStep4Page = () => {
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState(null);
  const [activeSettings, setActiveSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManual, setIsManual] = useState(null);
  const [inputData, setInputData] = useState(null);
  const [formFields, setFormFields] = useState(null);
  const [formFieldsObject, setFormFieldsObject] = useState({});
  const notification = useNotification();
  const { id, step } = useParams();
  const loader = useLoader();
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useDispatch();
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV2 = version === API_VERSION.V2;
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const [
    activateProject,
    { isLoading: isLoadingActivateProject, isSuccess: isSuccessActivateProject },
  ] = useActivateProjectMutation();
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } = (
    useGetDefaultConfigurationTemplateFieldsQuery as any
  )();
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      { id, page: settingsPage },
      { skip: !Boolean(id || defaultTemplateFields), refetchOnMountOrArgChange: true },
    );
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings && activeSettings.id },
      {
        skip: !Boolean(id && activeSettings && defaultTemplateFields),
        refetchOnMountOrArgChange: true,
      },
    );
  const { data: customTemplatesData, isLoading: isLoadingCustomTemplates } = (
    useGetCustomTemplatesQuery as any
  )();

  useEffect(() => {
    if (isSuccessActivateProject) {
      notification.success('Project has been activated!');
    }
  }, [isSuccessActivateProject]);

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingCustomTemplates ||
      isLoadingActivateProject ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingConfigurationSettingsPrompt,
    isLoadingCustomTemplates,
    isLoadingActivateProject,
    isManual,
    settings,
  ]);

  useEffect(() => {
    if (currentProject && !currentProject.is_active) {
      activateProject({ projectId: id });
    }
  }, [currentProject]);

  useEffect(() => {
    if (defaultTemplateFields) {
      const fields = (defaultTemplateFields as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));
      setFormFields(fields);
      setInputData(null);
    }
  }, [defaultTemplateFields]);

  useEffect(() => {
    if (formFields) {
      const fieldsObject = formFields.reduce((prev, current) => {
        prev[current.key] = current;

        return prev;
      }, {});

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (settings) {
      const config = creationFlowState[id] || {};
      const settingsId = isManual ? config.manualSettingsId : config.promptBuilderSettingsId;

      if (settings.length) {
        if (settingsId) {
          const setting = settings.find((setting) => `${setting.id}` === `${settingsId}`);

          setting && setActiveSettings(setting);
        }
      }
    }
  }, [settings, isManual, creationFlowState]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const fields = (configurationSettingsPrompt as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));

      setFormFields(fields);
      setInputData((configurationSettingsPrompt as any).input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (allConfigurationSettings) {
      setSettings((prevState) => {
        const prev = prevState || [];
        const newState = [...prev];

        (allConfigurationSettings as any).items.forEach((item) => {
          const index = prev.findIndex((prevItem) => {
            return prevItem.id === item.id;
          });

          if (index > -1) {
            newState[index] = item;
          } else {
            newState.push(item);
          }
        });

        newState.sort((a, b) => +moment(b.created_at) - +moment(a.created_at));

        return newState;
      });

      if ((allConfigurationSettings as any).page_info.has_next_page) {
        setSettingsPage((allConfigurationSettings as any).page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && config.promptType) {
      setIsManual(config.promptType === 'manual');
    }

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, [creationFlowState]);

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <CreateProjectStepsTitle
              step={isV2 ? +step - 1 : +step}
              title={isManual ? 'System Prompt' : 'AI Role'}
            />
            <CreateProjectHead step={+step} titleIndex={isManual ? 3 : 4} />
            <div
              className={clsx('flex flex-col w-full', isManual ? 'max-w-[500px]' : 'max-w-[455px]')}
            >
              {isManual ? (
                <CreateProjectManualStep4
                  configurationSettingsPrompt={configurationSettingsPrompt}
                  customTemplatesData={customTemplatesData}
                />
              ) : (
                <CreateProjectPromptBuilderStep4
                  settings={settings}
                  activeSettings={activeSettings}
                  configurationSettingsPrompt={configurationSettingsPrompt}
                  inputData={inputData}
                  defaultTemplateFields={defaultTemplateFields}
                  formFieldsObject={formFieldsObject}
                />
              )}
            </div>
          </>
        )}
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep4Page;
